/*!
 * Coverage Calculator v2
 * Metaobject-driven coverage calculator with inline variant selectors
 * and add-to-cart support.
 *
 * Flow:
 *   1. Parse variant/option data from JSON islands
 *   2. Render pill selectors for non-size options (filtered to only
 *      combinations that actually exist + are available)
 *   3. Pre-select the main product's currently-selected variant
 *   4. On Calculate: filter matching variants, run tin-picking algorithm
 *      using custom.volume metafield, render recommendation card
 *   5. Add-to-Cart: POST main + primer to /cart/add.js, open drawer
 */
(function () {
  'use strict';

  const calcs = document.querySelectorAll('.ccv2');
  if (!calcs.length) return;

  calcs.forEach(initCalculator);

  function initCalculator(root) {
    // Parse config from data attributes
    const config = {
      coverageRate: parseFloat(root.dataset.coverageRate) || 6,
      coverageUnit: root.dataset.coverageUnit || 'Litres',
      defaultCoats: parseInt(root.dataset.defaultCoats, 10) || 2,
      maxCoats: parseInt(root.dataset.maxCoats, 10) || 3,
      hasPrimer: root.dataset.hasPrimer === 'true',
      primerRate: parseFloat(root.dataset.primerRate) || 0,
      primerName: root.dataset.primerName || '',
      primerUrl: root.dataset.primerUrl || '',
      primerHandle: root.dataset.primerHandle || '',
      sizeOptionIndex: parseInt(root.dataset.sizeOptionIndex, 10),
      initialVariantId: parseInt(root.dataset.initialVariantId, 10),
      productId: parseInt(root.dataset.productId, 10)
    };

    // Parse JSON islands
    const variants = parseJSON(root.querySelector('[data-ccv2-variants]'));
    const options = parseJSON(root.querySelector('[data-ccv2-options]'));
    const primerVariants = config.hasPrimer
      ? parseJSON(root.querySelector('[data-ccv2-primer-variants]'))
      : [];

    if (!variants || !options) {
      console.error('[ccv2] Missing variant or options data');
      return;
    }

    // DOM refs
    const modeInputs = root.querySelectorAll('[data-ccv2-mode]');
    const areaField = root.querySelector('[data-ccv2-field="area"]');
    const dimensionsField = root.querySelector('[data-ccv2-field="dimensions"]');
    const sqmInput = root.querySelector('[data-ccv2-sqm]');
    const widthInput = root.querySelector('[data-ccv2-width]');
    const heightInput = root.querySelector('[data-ccv2-height]');
    const coatsInput = root.querySelector('[data-ccv2-coats]');
    const calculateBtn = root.querySelector('[data-ccv2-calculate]');
    const output = root.querySelector('[data-ccv2-output]');
    const selectorsWrap = root.querySelector('[data-ccv2-variant-selectors]');

    // Internal state — user's current selection for each non-size option
    const selection = {};

    // ----------------------------------------------------------------
    // INIT: render selectors, pre-fill from initial variant
    // ----------------------------------------------------------------

    renderVariantSelectors();
    setInitialSelection();

    // ----------------------------------------------------------------
    // Area mode toggle
    // ----------------------------------------------------------------
    modeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const mode = input.value;
        areaField.hidden = mode !== 'area';
        dimensionsField.hidden = mode !== 'dimensions';
      });
    });

    // Enter key triggers calculate
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('.ccv2-input')) {
        e.preventDefault();
        calculate();
      }
    });

    calculateBtn.addEventListener('click', calculate);

    // ================================================================
    // Variant selector rendering
    // ================================================================

    function renderVariantSelectors() {
      selectorsWrap.innerHTML = '';

      // Find non-size options with more than one distinct value among
      // available variants. Options with only one value aren't worth
      // a selector.
      const nonSizeOptions = options.filter((opt) => {
        if (opt.is_size) return false;
        const valuesInStock = new Set();
        variants.forEach((v) => {
          if (v.available) valuesInStock.add(v.options[opt.position]);
        });
        return valuesInStock.size > 1;
      });

      if (nonSizeOptions.length === 0) return;

      // Divider heading
      const heading = document.createElement('p');
      heading.className = 'ccv2-variant-heading';
      heading.textContent = 'Which version?';
      selectorsWrap.appendChild(heading);

      nonSizeOptions.forEach((opt) => {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'ccv2-fieldset';

        const legend = document.createElement('legend');
        legend.className = 'ccv2-legend';
        legend.textContent = opt.name;
        fieldset.appendChild(legend);

        const group = document.createElement('div');
        group.className = 'ccv2-radio-group ccv2-radio-group--wrap';

        opt.values.forEach((value) => {
          // Hide value entirely if no available variant has it
          const hasAvailable = variants.some(
            (v) => v.available && v.options[opt.position] === value
          );
          if (!hasAvailable) return;

          const label = document.createElement('label');
          label.className = 'ccv2-radio';

          const input = document.createElement('input');
          input.type = 'radio';
          input.name = 'ccv2-opt-' + opt.position + '-' + config.productId;
          input.value = value;
          input.dataset.ccv2Option = opt.position;

          const span = document.createElement('span');
          span.textContent = value;

          input.addEventListener('change', () => {
            selection[opt.position] = value;
            // Future: could cross-filter other option selectors here
            // based on what's available in the new combo. Keeping
            // simple for now.
          });

          label.appendChild(input);
          label.appendChild(span);
          group.appendChild(label);
        });

        fieldset.appendChild(group);
        selectorsWrap.appendChild(fieldset);
      });
    }

    function setInitialSelection() {
      // Find the initially-selected variant and prefill the selectors
      const initial = variants.find((v) => v.id === config.initialVariantId);
      if (!initial) {
        // Fallback to first available variant
        const firstAvailable = variants.find((v) => v.available);
        if (firstAvailable) {
          initial && void 0;
          options.forEach((opt) => {
            if (!opt.is_size) {
              selection[opt.position] = firstAvailable.options[opt.position];
            }
          });
        }
      } else {
        options.forEach((opt) => {
          if (!opt.is_size) {
            selection[opt.position] = initial.options[opt.position];
          }
        });
      }

      // Check the matching radio buttons
      Object.keys(selection).forEach((pos) => {
        const radio = selectorsWrap.querySelector(
          'input[data-ccv2-option="' + pos + '"][value="' +
          cssEscape(selection[pos]) + '"]'
        );
        if (radio) radio.checked = true;
      });
    }

    // ================================================================
    // Calculate
    // ================================================================

    function calculate() {
      // Resolve area (m²)
      const mode = root.querySelector('[data-ccv2-mode]:checked').value;
      let sqm = 0;
      if (mode === 'area') {
        sqm = parseFloat(sqmInput.value) || 0;
      } else {
        const w = parseFloat(widthInput.value) || 0;
        const h = parseFloat(heightInput.value) || 0;
        sqm = w * h;
      }

      if (sqm <= 0) {
        showError('Please enter a valid area.');
        return;
      }

      const coats = Math.max(1, parseInt(coatsInput.value, 10) || config.defaultCoats);

      // Primer required?
      let primerRequired = false;
      if (config.hasPrimer) {
        const primerChecked = root.querySelector('[data-ccv2-primer]:checked');
        primerRequired = primerChecked && primerChecked.value === 'yes';
      }

      // Main product quantity (round up to 0.5 for safe purchase)
      const productQty = roundUpHalf((sqm / config.coverageRate) * coats);

      // Filter variants matching user's non-size selections
      const matchingVariants = variants.filter((v) => {
        if (!v.available) return false;
        if (v.volume == null || v.volume <= 0) return false;
        return Object.keys(selection).every(
          (pos) => v.options[pos] === selection[pos]
        );
      });

      if (matchingVariants.length === 0) {
        showError("Sorry, we couldn't find a matching size in this version. Please try a different combination.");
        return;
      }

      // Pick tins for main product
      const mainPick = pickTins(matchingVariants, productQty);
      if (!mainPick) {
        showError('Unable to cover the required quantity with available sizes.');
        return;
      }

      // Primer tin-picking (if required)
      let primerPick = null;
      let primerQty = 0;
      if (primerRequired && config.primerRate > 0 && primerVariants.length > 0) {
        primerQty = roundUpHalf(sqm / config.primerRate);
        const availablePrimers = primerVariants.filter(
          (v) => v.available && v.volume != null && v.volume > 0
        );
        if (availablePrimers.length > 0) {
          primerPick = pickTins(availablePrimers, primerQty);
        }
      }

      renderRecommendation({
        productQty,
        coats,
        mainPick,
        primerRequired,
        primerQty,
        primerPick
      });
    }

    // ================================================================
    // Tin-picking algorithm
    //
    // Greedy: use as many of the largest available size as fit, then
    // fall back to smaller sizes for the remainder. Prefers the fewest
    // tins that cover the needed quantity.
    // ================================================================

    function pickTins(available, needed) {
      // Sort largest volume first for enumeration
      const sorted = [...available].sort((a, b) => b.volume - a.volume);

      // Enumerate all reasonable combinations. A combination is
      // "reasonable" if it covers the need and every tin in it is
      // actually doing useful work (i.e. removing a tin would make
      // the combo fail to cover the need).
      //
      // Bound the search: for each size, max qty needed is
      // ceil(needed / size). Small search space in practice —
      // most products have 2-4 sizes.
      const combinations = [];
      const maxQtys = sorted.map(
        (v) => Math.ceil(needed / v.volume)
      );

      function recurse(index, current, volumeSoFar, priceSoFar) {
        // Covered? Record this combo.
        if (volumeSoFar >= needed) {
          // Filter out combos where any tin is redundant.
          // A tin is redundant if removing one of it still covers need.
          const pruned = current
            .map((p) => ({ ...p }))
            .filter((p) => p.qty > 0);

          const reallyNeeded = pruned.every((p) => {
            return volumeSoFar - p.variant.volume < needed - 0.001;
          });

          if (reallyNeeded) {
            combinations.push({
              picks: pruned,
              totalVolume: volumeSoFar,
              totalPrice: priceSoFar
            });
          }
          return;
        }

        // Ran out of sizes without covering — abandon branch
        if (index >= sorted.length) return;

        const v = sorted[index];
        const maxQty = maxQtys[index];

        // Try 0..maxQty of this size, then recurse into next
        for (let q = 0; q <= maxQty; q++) {
          const nextCurrent = q === 0
            ? current
            : [...current, { variant: v, qty: q }];
          recurse(
            index + 1,
            nextCurrent,
            volumeSoFar + q * v.volume,
            priceSoFar + q * v.price
          );
        }
      }

      recurse(0, [], 0, 0);

      if (combinations.length === 0) return null;

      // Score each combination — lower is better.
      // Primary: total price (trade customers want cheapest way to
      // cover the need, and large tins are often cheaper per litre
      // so overbuying a big tin can beat buying several smaller ones).
      // Tiebreaker: fewer tins (less handling).
      const score = (combo) => {
        const tinCount = combo.picks.reduce((sum, p) => sum + p.qty, 0);
        // Price is in pence (Shopify stores money in smallest unit),
        // tin count adds 0.01p per tin as a near-zero tiebreaker so
        // it only matters when prices are exactly equal.
        return combo.totalPrice + tinCount * 0.01;
      };

      combinations.sort((a, b) => score(a) - score(b));
      return combinations[0];
    }

    // ================================================================
    // Rendering the recommendation card
    // ================================================================

    function renderRecommendation(data) {
      const {
        productQty, coats, mainPick,
        primerRequired, primerQty, primerPick
      } = data;

      const parts = [];

      parts.push(
        '<p class="ccv2-output-label">You\'ll need approximately:</p>',
        '<div class="ccv2-output-result">',
        '<span class="ccv2-main-line"><strong>' + productQty + ' ' +
        escapeHtml(config.coverageUnit) + '</strong> based on ' + coats +
        ' coat' + (coats === 1 ? '' : 's') + '</span>'
      );

      // Primer coverage line — only if primer was requested and calculable
      if (primerRequired && primerQty > 0) {
        const primerLabel = config.primerName
          ? (config.primerUrl
              ? '<a href="' + escapeAttr(config.primerUrl) +
                '" class="ccv2-primer-link">' +
                escapeHtml(config.primerName) + '</a>'
              : escapeHtml(config.primerName))
          : 'primer';

        parts.push(
          '<span class="ccv2-primer-line">Plus ' +
          '<strong>' + primerQty + ' ' + escapeHtml(config.coverageUnit) +
          '</strong> of ' + primerLabel + '</span>'
        );
      }

      parts.push('</div>');

      // Recommended purchase section
      parts.push('<div class="ccv2-rec">');
      parts.push('<p class="ccv2-rec-title">Recommended purchase</p>');
      parts.push('<div class="ccv2-rec-items">');

      mainPick.picks.forEach((pick) => {
        parts.push(renderTinCard(pick, 'main'));
      });

      if (primerRequired && primerPick) {
        primerPick.picks.forEach((pick) => {
          parts.push(renderTinCard(pick, 'primer'));
        });
      } else if (primerRequired && !primerPick) {
        parts.push(
          '<p class="ccv2-rec-warning">Primer coverage not available — ' +
          (config.primerUrl
            ? '<a href="' + escapeAttr(config.primerUrl) + '">see the primer page</a>'
            : 'please contact us') +
          ' for advice.</p>'
        );
      }

      parts.push('</div>');

      // Total + add to cart
      const grandTotal = mainPick.totalPrice + (primerPick ? primerPick.totalPrice : 0);
      parts.push(
        '<div class="ccv2-rec-actions">',
        '<p class="ccv2-rec-total">Total: <strong>' + formatMoney(grandTotal) + '</strong></p>',
        '<button type="button" class="ccv2-add-btn" data-ccv2-add-to-cart>Add to basket</button>',
        '</div>'
      );

      parts.push('</div>');

      // Smallprint
      parts.push(
        '<p class="ccv2-output-smallprint">*Estimate only. Actual coverage ' +
        'varies based on surface porosity, application method, and absorption. ' +
        'Recommended purchase rounds up to the nearest tin size so you have ' +
        'enough to finish the job plus some for touch-ups.</p>'
      );

      output.innerHTML = parts.join('');
      output.hidden = false;

      // Release the sticky sidebar once a result is shown — otherwise
      // the calculator can be taller than the viewport and the user
      // can't scroll to the Add to Basket button.
      const stickyParent = root.closest('.ptv2-sidebar__sticky');
      if (stickyParent) stickyParent.classList.add('ccv2-unstuck');

      // Wire up add to cart
      const addBtn = output.querySelector('[data-ccv2-add-to-cart]');
      if (addBtn) {
        addBtn.addEventListener('click', () => {
          addToCart(mainPick, primerRequired ? primerPick : null, addBtn);
        });
      }

      // Scroll into view on mobile
      if (window.innerWidth < 1024) {
        output.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    function renderTinCard(pick, type) {
      const { variant, qty } = pick;
      const label = type === 'primer'
        ? escapeHtml(config.primerName || 'Primer')
        : '';

      const parts = [
        '<div class="ccv2-tin ccv2-tin--' + type + '">'
      ];

      if (variant.image) {
        parts.push('<img src="' + escapeAttr(variant.image) + '" alt="" class="ccv2-tin-img" loading="lazy">');
      } else {
        parts.push('<div class="ccv2-tin-img ccv2-tin-img--placeholder"></div>');
      }

      parts.push(
        '<div class="ccv2-tin-info">',
        label ? '<p class="ccv2-tin-kind">' + label + '</p>' : '',
        '<p class="ccv2-tin-title">' + qty + ' × ' + escapeHtml(variant.title) + '</p>',
        '<p class="ccv2-tin-price">' + escapeHtml(variant.price_formatted) +
        (qty > 1 ? ' each' : '') + '</p>',
        '</div>',
        '</div>'
      );

      return parts.join('');
    }

    // ================================================================
    // Add to cart
    // ================================================================

    async function addToCart(mainPick, primerPick, btn) {
      const items = [];

      mainPick.picks.forEach((p) => {
        items.push({ id: p.variant.id, quantity: p.qty });
      });

      if (primerPick) {
        primerPick.picks.forEach((p) => {
          items.push({ id: p.variant.id, quantity: p.qty });
        });
      }

      if (items.length === 0) return;

      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        // Sections for cart drawer refresh
        const sectionsToRender = getSectionsToRender();

        const body = { items };
        if (sectionsToRender.length) {
          body.sections = sectionsToRender.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || 'Failed to add to cart');
        }

        const data = await response.json();

        // Refresh cart drawer if we can find it
        refreshCartDrawer(data);

        // Fire a standard Shopify cart:updated event so anything listening
        // (cart bubble, etc) picks up the change
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { source: 'coverage-calculator-v2' }
        }));

        // Show a proper confirmation message in the recommendation card
        showAddedConfirmation(btn);

      } catch (err) {
        console.error('[ccv2] Add to cart failed:', err);
        btn.disabled = false;
        btn.textContent = originalText;
        alert('Sorry, we couldn\'t add these to your basket. Please try again or contact us.');
      }
    }

    function showAddedConfirmation(btn) {
      // Find the actions container (holds the button) and replace its
      // contents with a success state so the user gets clear feedback.
      const actions = btn.closest('.ccv2-rec-actions');
      if (!actions) return;

      actions.innerHTML =
        '<div class="ccv2-added">' +
          '<div class="ccv2-added-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
              '<polyline points="20 6 9 17 4 12"></polyline>' +
            '</svg>' +
          '</div>' +
          '<div class="ccv2-added-text">' +
            '<p class="ccv2-added-title">Added to your basket</p>' +
            '<p class="ccv2-added-sub">' +
              '<a href="/cart" class="ccv2-added-link">View basket</a>' +
              ' or keep browsing — calculate again to re-add' +
            '</p>' +
          '</div>' +
        '</div>';
    }

    function getSectionsToRender() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        return drawer.getSectionsToRender().map((s) => s.section);
      }
      // Common fallbacks
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    function refreshCartDrawer(addResponse) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      if (typeof drawer.renderContents === 'function' && addResponse.sections) {
        drawer.renderContents(addResponse);
      }

      // Open the drawer if the component supports it
      if (typeof drawer.open === 'function') {
        drawer.open();
      }
    }

    // ================================================================
    // Error display
    // ================================================================

    function showError(message) {
      output.innerHTML =
        '<p class="ccv2-output-label">Oops:</p>' +
        '<div class="ccv2-output-result"><span class="ccv2-main-line">' +
        escapeHtml(message) + '</span></div>';
      output.hidden = false;
    }
  }

  // ================================================================
  // Helpers
  // ================================================================

  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[ccv2] JSON parse failed:', e);
      return null;
    }
  }

  function roundUpHalf(n) {
    return Math.ceil(n * 2) / 2;
  }

  function formatMoney(cents) {
    const pounds = (cents / 100).toFixed(2);
    return '£' + pounds;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(str) {
    return escapeHtml(str);
  }

  function cssEscape(str) {
    // Minimal attr value escape — escape quote chars used in the selector
    return String(str).replace(/"/g, '\\"');
  }
})();